import React, { useEffect, useRef, useState } from "react";
import { Button, Form, ProgressBar, Badge, Card, ListGroup } from "react-bootstrap";
import Icon from "../Icon";
import { faPlus as add, faTrash as trash, faCheck, faBullseye, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { LifePartSuccessSign } from "@/state/LifePart";

type Props = {
    successSigns: LifePartSuccessSign[];
    onChange: (signs: LifePartSuccessSign[]) => void;
};

export default function SuccessSignsWidget({ successSigns, onChange }: Props) {
    const [isEditing, setIsEditing] = useState(false);
    const [draftSigns, setDraftSigns] = useState<LifePartSuccessSign[]>(successSigns);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Синхронизируем draft с пропсами при выходе из режима редактирования
    useEffect(() => {
        if (!isEditing) {
            setDraftSigns(successSigns);
        }
    }, [successSigns, isEditing]);

    const activeSigns = isEditing ? draftSigns : successSigns;
    const doneCount = activeSigns.filter((s) => s.isDone).length;
    const totalCount = activeSigns.length;
    const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

    const handleAdd = () => {
        const newSigns = [...draftSigns, { isDone: false, title: "" }];
        setDraftSigns(newSigns);
        setTimeout(() => {
            const newIndex = newSigns.length - 1;
            inputRefs.current[newIndex]?.focus();
        }, 0);
    };

    const handleRemove = (index: number) => {
        const newSigns = [...draftSigns];
        newSigns.splice(index, 1);
        setDraftSigns(newSigns);
    };

    const handleDraftChange = (index: number, field: keyof LifePartSuccessSign, value: boolean | string) => {
        const newSigns = [...draftSigns];
        newSigns[index] = { ...newSigns[index], [field]: value };
        setDraftSigns(newSigns);
    };

    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const newSigns = [
                ...draftSigns.slice(0, index + 1),
                { isDone: false, title: "" },
                ...draftSigns.slice(index + 1),
            ];
            setDraftSigns(newSigns);
            setTimeout(() => {
                inputRefs.current[index + 1]?.focus();
            }, 0);
        }
    };

    const startEditing = () => {
        setDraftSigns(successSigns);
        setIsEditing(true);
    };

    const finishEditing = () => {
        // Удаляем пустые критерии при выходе из режима редактирования
        const cleaned = draftSigns.filter((s) => s.title.trim().length > 0);
        onChange(cleaned);
        setIsEditing(false);
    };

    const handleToggleDone = (index: number) => {
        // В режиме просмотра — сразу сохраняем
        if (!isEditing) {
            const newSigns = [...successSigns];
            newSigns[index] = { ...newSigns[index], isDone: !newSigns[index].isDone };
            onChange(newSigns);
        } else {
            // В режиме редактирования — обновляем draft
            const newSigns = [...draftSigns];
            newSigns[index] = { ...newSigns[index], isDone: !newSigns[index].isDone };
            setDraftSigns(newSigns);
        }
    };

    const allDone = doneCount === totalCount && totalCount > 0;

    return (
        <Card className="border-2 shadow-sm" style={{ borderColor: allDone ? "#198754" : "var(--bs-primary)" }}>
            <Card.Header
                className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2"
                style={{ backgroundColor: allDone ? "rgba(25, 135, 84, 0.08)" : "rgba(13, 110, 253, 0.08)" }}
            >
                <div className="d-flex align-items-center gap-2">
                    <Icon icon={faBullseye} style={{ color: allDone ? "#198754" : "var(--bs-primary)" }} />
                    <strong style={{ color: allDone ? "#198754" : "var(--bs-primary)" }}>
                        Цели
                    </strong>
                    {totalCount > 0 && (
                        <Badge bg={allDone ? "success" : "primary"}>
                            {doneCount}/{totalCount}
                        </Badge>
                    )}
                </div>
                <div className="d-flex gap-2">
                    {totalCount > 0 || isEditing ? (
                        <Button
                            variant={isEditing ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => isEditing ? finishEditing() : startEditing()}
                        >
                            {isEditing ? "Готово" : (
                                <><Icon icon={faPenToSquare} className="me-1" /> Редактировать</>
                            )}
                        </Button>
                    ) : null}
                </div>
            </Card.Header>

            <Card.Body>
                {totalCount === 0 ? (
                    <div className="text-center py-4">
                        <div className="text-muted mb-2">
                            <Icon icon={faBullseye} style={{ fontSize: "1.5rem", opacity: 0.4 }} />
                        </div>
                        <p className="text-muted mb-3">
                            <strong>Цели</strong> — это результаты, которые Вы намерены достичь за период планирования
                            <br />
                            Когда все цели достигнуты, вы готовы поставить <strong>10 из 10</strong>.
                        </p>
                        <Button variant="primary" size="sm" onClick={() => { setDraftSigns([{ isDone: false, title: "" }]); setIsEditing(true); }}>
                            <Icon icon={add} className="me-1" /> Добавить первую цель
                        </Button>
                    </div>
                ) : (
                    <>
                        <ProgressBar
                            now={progress}
                            label={`${progress}%`}
                            variant={allDone ? "success" : "primary"}
                            className="mb-3"
                            style={{ height: 8 }}
                        />
                        <ListGroup variant="flush">
                            {activeSigns.map((sign, index) => (
                                <ListGroup.Item
                                    key={index}
                                    className={`px-2 py-2 border-0 ${sign.isDone ? "bg-success-subtle" : "bg-warning-subtle"
                                        }`}
                                    style={{
                                        borderLeft: `4px solid ${sign.isDone ? "#198754" : "#ffc107"} !important`,
                                        marginBottom: 4,
                                        borderRadius: 4,
                                    }}
                                >
                                    {isEditing ? (
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="flex-shrink-0">
                                                <Form.Check
                                                    checked={sign.isDone}
                                                    onChange={() => handleToggleDone(index)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            </div>
                                            <div className="flex-grow-1">
                                                {sign.isDone ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="text-decoration-line-through text-success">
                                                            {sign.title || "(без описания)"}
                                                        </span>
                                                        <Icon icon={faCheck} style={{ color: "#198754", fontSize: "0.8rem" }} />
                                                    </div>
                                                ) : (
                                                    <Form.Control
                                                        type="text"
                                                        value={sign.title}
                                                        onChange={(e) => handleDraftChange(index, "title", e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                                        placeholder="Какова Ваша цель?"
                                                        size="sm"
                                                        ref={(el) => { inputRefs.current[index] = el; }}
                                                        className="border-warning"
                                                    />
                                                )}
                                            </div>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="flex-shrink-0"
                                                onClick={() => handleRemove(index)}
                                            >
                                                <Icon icon={trash} />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="flex-shrink-0">
                                                <Form.Check
                                                    checked={sign.isDone}
                                                    onChange={() => handleToggleDone(index)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            </div>
                                            <div className="flex-grow-1">
                                                {sign.isDone ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="text-decoration-line-through text-success">
                                                            {sign.title || "(без описания)"}
                                                        </span>
                                                        <Icon icon={faCheck} style={{ color: "#198754", fontSize: "0.8rem" }} />
                                                    </div>
                                                ) : (
                                                    <span className={sign.title ? "" : "text-muted fst-italic"}>
                                                        {sign.title || "(без описания)"}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                        {isEditing && (
                            <div className="mt-3">
                                <Button variant="outline-success" size="sm" onClick={handleAdd}>
                                    <Icon icon={add} className="me-1" /> Добавить цель
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
